import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PdsBox, PdsNotification } from '@podium-design-system/react-components';

/** Alert steps from 90% through 100% in 2% increments. */
const QUOTA_ALERT_STEPS = [90, 92, 94, 96, 98, 100];

const STORAGE_PREFIX = 'argus.apiQuota.lastMilestone.';

const POLL_MS = 3 * 60 * 1000;

function formatLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Same rounding as backend ApiLimitsService.pct */
function usagePercent(used, limit) {
  if (limit == null || limit <= 0) return null;
  return Math.round((Number(used) * 10000) / limit) / 100;
}

/**
 * Highest alert step not above current usage (e.g. 93% → 92).
 */
export function currentQuotaAlertMilestone(pct) {
  if (pct == null || Number.isNaN(pct) || pct < 90) return null;
  let m = null;
  for (const step of QUOTA_ALERT_STEPS) {
    if (pct >= step) m = step;
  }
  return m;
}

const TARGETS = [
  {
    provider: 'SECURITY_TRAILS',
    basis: 'MONTHLY',
    displayName: 'SecurityTrails',
    basisPhrase: 'monthly',
  },
  {
    provider: 'PUBLICWWW',
    basis: 'DAILY',
    displayName: 'PublicWWW',
    basisPhrase: 'daily',
  },
];

/**
 * Polls API limits overview and shows a dismissible warning when usage crosses a new
 * 90% / 92% / … / 100% step for SecurityTrails (monthly) or PublicWWW (daily).
 * Does not block tool usage. Uses sessionStorage so each step alerts once until usage drops.
 */
export default function ApiQuotaApproachingAlerts() {
  const [toasts, setToasts] = useState([]);

  const dismiss = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const runCheck = useCallback(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;

    const today = new Date();
    const startDate = formatLocalYmd(new Date(today.getFullYear(), today.getMonth(), 1));
    const endDate = formatLocalYmd(today);

    axios
      .get('/api/api-limits/stats', {
        params: { startDate, endDate, includeVendorSnapshots: false },
      })
      .then((res) => {
        const overview = res.data?.overview;
        if (!Array.isArray(overview)) return;

        const newToasts = [];

        for (const target of TARGETS) {
          const row = overview.find((r) => r.provider === target.provider && r.basis === target.basis);
          if (!row) continue;

          const pct = usagePercent(row.used, row.limit);
          const M = currentQuotaAlertMilestone(pct);
          const key = STORAGE_PREFIX + target.provider;

          if (pct == null || pct < 90) {
            sessionStorage.removeItem(key);
            continue;
          }
          if (M == null) continue;

          const raw = sessionStorage.getItem(key);
          const stored = raw != null && raw !== '' ? parseInt(raw, 10) : -1;

          if (M < stored) {
            sessionStorage.setItem(key, String(M));
            continue;
          }
          if (M > stored) {
            sessionStorage.setItem(key, String(M));
            newToasts.push({
              id: `${target.provider}-${M}-${Date.now()}`,
              message: (
                <>
                  <strong>{target.displayName}</strong> {target.basisPhrase} API usage is at{' '}
                  <strong>{pct.toFixed(1)}%</strong> of quota (alert step {M}%). Plan remaining calls or review
                  limits on the{' '}
                  <Link to="/api-limits" style={{ fontWeight: 600, textDecoration: 'underline' }}>
                    API Limits
                  </Link>{' '}
                  page.
                </>
              ),
            });
          }
        }

        if (newToasts.length > 0) {
          setToasts((prev) => [...prev, ...newToasts]);
        }
      })
      .catch(() => {
        /* best-effort; no user-facing error */
      });
  }, []);

  useEffect(() => {
    runCheck();
    const id = window.setInterval(runCheck, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') runCheck();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [runCheck]);

  if (toasts.length === 0) return null;

  return (
    <PdsBox direction="column" gap="xs" style={{ padding: '0 16px 12px', maxWidth: 960, margin: '0 auto', width: '100%' }}>
      {toasts.map((t) => (
        <PdsNotification key={t.id} color="warning" onDismiss={() => dismiss(t.id)} size="sm">
          {t.message}
        </PdsNotification>
      ))}
    </PdsBox>
  );
}
